import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import AppIcon from '@atlaskit/icon/core/app';
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type EditorToolbarHandlerContext,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

export const MAUI_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('maui');

const isMauiUrl = (url: string): boolean => /\/remix\/ui\/[A-Za-z0-9_-]+/.test(url);

/**
 * Experience manifest for a MAUI embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(MauiExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const MauiExperienceManifest = {
	id: MAUI_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isMauiUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: () => true,
	shouldAutoConvertToNativeEmbed: true,
	// remove this when MAUI is onboarded to the Linking Platform. This is here to allow testing before it is onboarded.
	resolveViaLinkingPlatform: false,
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			customActions: {},
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.COPY_LINK,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
			moreItems: [],
		}),
		editorToolbarHandlers: {
			onAskRovoClick: (
				_parameters: NativeEmbedParameterValues,
				context?: EditorToolbarHandlerContext,
			) => {
				const editorApi = context?.editorApi as PublicPluginAPI<[AIPlugin]>;
				editorApi?.aiExperience?.actions?.openAIModal?.({
					invokedFrom: 'floatingToolbar',
				});
			},
		},
		titleIcon: AppIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
